import 'react-datepicker/dist/react-datepicker.css'
import './WeeklyPicker.css'
import { ChevronUpDownIcon } from '@heroicons/react/24/outline'
import { endOfWeek, format, getYear, startOfDay, subDays, subWeeks } from 'date-fns'
import {
  ComponentPropsWithoutRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import ReactDatePicker from 'react-datepicker'

import { cn } from '@sparkai/utils'

import { DateInterval } from '../DatePicker/DatePicker'
import { WeeklyPickerHeader } from './WeeklyPickerHeader'

type DateRangeTuple = [Date | null, Date | null]

export interface WeeklyPickerProps {
  value?: DateRangeTuple
  onChange: (date: DateRangeTuple) => void
  placeholder?: string

  showTimeSelect?: boolean
  timeIntervals?: number
  monthsShown?: number
  showTimeInput?: boolean

  excludeDates?: Date[]
  excludeDateIntervals?: DateInterval[]
  filterDate?: (date: Date) => boolean

  excludeTimes?: Date[]
  filterTime?: (date: Date) => boolean

  shouldCloseOnSelect?: boolean
  showMonthAndYearSelector?: boolean
  timeFormat?: string
  dateFormat?: string

  maxDate?: Date | null
  minDate?: Date | null

  className?: string
  weeksDefaultRange?: number
  error?: boolean

  hideInput?: boolean
  autoShow?: boolean
  calendarTheme?: 'full' | 'shadow'
  direction?: 'backward' | 'forward'
  referenceDate?: Date
}

export const getWeeksRange = (weeks: number, referenceDate = new Date()): [Date, Date] => {
  const today = startOfDay(referenceDate)
  const lastDayOfWeek = endOfWeek(today)
  const first = startOfDay(subDays(subWeeks(lastDayOfWeek as Date, weeks), -1))

  return [first, lastDayOfWeek]
}

export function WeeklyPicker({
  value,
  onChange,
  placeholder,
  weeksDefaultRange = 8,
  error,
  hideInput,
  autoShow,
  calendarTheme = 'shadow',
  direction = 'backward',
  referenceDate = new Date(),
  ...props
}: Readonly<WeeklyPickerProps>) {
  const startDate = value?.[0]
  const endDate = value?.[1]
  const datePickerRef = useRef<ReactDatePicker>(null)

  const [isRangeSelected, setIsRangeSelected] = useState(false)

  const weekLabel = `${weeksDefaultRange} weeks`

  const formatPattern =
    endDate && startDate && getYear(endDate) - getYear(startDate) > 0 ? 'dd MMM yyyy' : 'dd MMM'

  const periodLabel =
    startDate && endDate ? `${format(startDate, formatPattern)} - ${format(endDate, formatPattern)}` : ''

  useEffect(() => {
    if (!value?.[0] && !value?.[1] && direction !== 'forward') {
      onChange(getWeeksRange(weeksDefaultRange, referenceDate))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className='date-weekly-picker-root'>
      <ReactDatePicker
        ref={datePickerRef}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        selected={endDate}
        showPopperArrow={false}
        customInput={hideInput ? <></> : <CustomInputSelect error={error} />}
        calendarClassName={cn({
          'w-full border-0 mt-[-20px]': calendarTheme === 'full',
          'shadow-xl ring-1 ring-black/5 p-6 border-0! rounded-md': calendarTheme === 'shadow',
        })}
        onChange={(update) => {
          if (onChange && update) {
            const selectedDate = update as unknown as DateRangeTuple
            const clicked = selectedDate[0]
            if (!clicked) return
            let newPeriod: DateRangeTuple
            if (direction === 'forward') {
              const end = endOfWeek(clicked)
              const start = startOfDay(subDays(subWeeks(end, weeksDefaultRange), -1))
              newPeriod = [start, end]
            } else {
              const lastDayOfWeek = endOfWeek(clicked)
              const first = startOfDay(subDays(subWeeks(lastDayOfWeek as Date, weeksDefaultRange), -1))
              newPeriod = [first, lastDayOfWeek]
            }
            onChange(newPeriod)
            setIsRangeSelected(false)
          }
        }}
        startDate={startDate}
        renderCustomHeader={(props) => (
          <WeeklyPickerHeader
            onClickRangeDates={(value) => {
              setIsRangeSelected(true)
              onChange && onChange(value as unknown as DateRangeTuple)
              !autoShow && datePickerRef?.current?.setOpen(false)
            }}
            isRangeSelected={isRangeSelected}
            rangeWeek={weeksDefaultRange}
            startDate={startDate}
            endDate={endDate}
            direction={direction}
            referenceDate={referenceDate}
            {...props}
          />
        )}
        monthsShown={1}
        endDate={endDate}
        placeholderText={placeholder}
        selectsRange
        open={autoShow}
        {...props}
      >
        {periodLabel && (
          <div className='ml-3'>
            <label className='font-normal text-gray-500'>Selected ({weekLabel}):</label>
            <label className='ml-9'>{periodLabel}</label>
          </div>
        )}
      </ReactDatePicker>
    </div>
  )
}

export type CustomInputSelectProps = ComponentPropsWithoutRef<'button'> & {
  error?: boolean
}

const CustomInputSelect = forwardRef<HTMLButtonElement, CustomInputSelectProps>(function CustomInputSelect(
  { value, error, ...props },
  ref
) {
  const innerRef = useRef<HTMLButtonElement>(null)
  // Just a trick to use ref while also forwarding
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  useImperativeHandle(ref, () => innerRef.current!)

  const valueFormatted = useMemo(() => {
    if (!value) return value

    const splitValues = (value as string).split('-')
    const start = new Date(splitValues[0].trim())
    const end = new Date(splitValues[1].trim())

    const diffYears = getYear(end) - getYear(start)
    const formatPattern = diffYears > 0 ? 'dd MMM yyyy' : 'dd MMM'

    return `${format(start, formatPattern)} - ${format(end, formatPattern)}`
  }, [value])

  return (
    <button
      ref={innerRef}
      type='button'
      {...props}
      className={cn(
        'chromatic-ignore mt-1 flex w-full min-w-[180px] cursor-pointer items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 font-inter text-sm text-gray-900 shadow-xs',
        {
          'border border-red-400': error,
        }
      )}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        innerRef.current?.focus()
      }}
      aria-label='open calendar modal'
    >
      <span>{valueFormatted}</span>

      <ChevronUpDownIcon className='h-5 w-5 font-bold text-gray-400' aria-hidden='true' />
    </button>
  )
})
